// pages/MusicLibraryPage.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, Search, Music, Loader, Disc3, X } from "lucide-react";
import api from "../../services/api";

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDuration = (seconds) => {
  if (!seconds) return "--:--";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// ── Waveform bars ─────────────────────────────────────────────────────────────
const WaveformBars = ({ isPlaying, count = 24 }) => {
  const heights = [
    3, 7, 5, 9, 4, 8, 6, 10, 3, 7, 5, 9, 6, 4, 8, 3, 9, 5, 7, 4, 10, 6, 3, 8,
  ];
  return (
    <div className="flex items-center gap-[2px] h-8">
      {heights.slice(0, count).map((h, i) => (
        <div
          key={i}
          className={`rounded-full flex-shrink-0 ${isPlaying ? "animate-pulse" : ""}`}
          style={{
            width: "2px",
            height: isPlaying ? `${h * 3}px` : "4px",
            backgroundColor: isPlaying ? "#a78bfa" : "rgba(255,255,255,0.12)",
            animationDelay: `${i * 35}ms`,
            transition: "height 0.2s ease, background-color 0.2s ease",
          }}
        />
      ))}
    </div>
  );
};

// ── Filter Chip ───────────────────────────────────────────────────────────────
const FilterChip = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap"
    style={{
      background: active ? "rgba(217,119,6,0.15)" : "rgba(255,255,255,0.04)",
      border: active
        ? "1px solid rgba(217,119,6,0.4)"
        : "1px solid rgba(255,255,255,0.07)",
      color: active ? "#fcd34d" : "#6b7280",
    }}
  >
    {label}
  </button>
);

const FilterSection = ({ title, items, selected, onSelect }) => {
  if (!items?.length) return null;
  return (
    <div className="mb-5">
      <p className="text-[10px] font-bold text-gray-700 uppercase tracking-widest mb-2">
        {title}
      </p>
      <div className="flex flex-col gap-1">
        <FilterChip
          label="All"
          active={!selected}
          onClick={() => onSelect(null)}
        />
        {items.map((item) => (
          <FilterChip
            key={item}
            label={item}
            active={selected === item}
            onClick={() => onSelect(selected === item ? null : item)}
          />
        ))}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const MusicLibraryPage = () => {
  const [tracks, setTracks] = useState([]);
  const [filters, setFilters] = useState({ genres: [], moods: [], tempos: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedTempo, setSelectedTempo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const audioRef = useRef(null);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(
      () => setDebouncedSearch(searchQuery),
      400,
    );
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => {
    loadTracks();
  }, [selectedGenre, selectedMood, selectedTempo, debouncedSearch]);

  const loadTracks = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedGenre) params.genre = selectedGenre;
      if (selectedMood) params.mood = selectedMood;
      if (selectedTempo) params.tempo = selectedTempo;
      if (debouncedSearch) params.search = debouncedSearch;
      const response = await api.getMusicLibrary(params);
      setTracks(response.tracks || []);
      setFilters(response.filters || { genres: [], moods: [], tempos: [] });
    } catch (err) {
      console.error("Failed to load music library:", err);
      setError("Failed to load tracks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = useCallback(
    (e, track) => {
      e?.stopPropagation();
      if (playingTrackId === track.id) {
        audioRef.current?.pause();
        setPlayingTrackId(null);
        return;
      }
      if (audioRef.current) audioRef.current.pause();
      audioRef.current = new Audio(track.audioUrl);
      audioRef.current.volume = 0.7;
      audioRef.current.play().catch(() => {});
      audioRef.current.addEventListener("ended", () => setPlayingTrackId(null));
      setPlayingTrackId(track.id);
    },
    [playingTrackId],
  );

  useEffect(() => () => audioRef.current?.pause(), []);

  const resetFilters = () => {
    setSelectedGenre(null);
    setSelectedMood(null);
    setSelectedTempo(null);
    setSearchQuery("");
  };
  const hasActiveFilters =
    selectedGenre || selectedMood || selectedTempo || searchQuery;

  return (
    <div
      className="flex flex-col h-full min-h-screen text-white"
      style={{ background: "#070a10" }}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-8 py-5 flex-shrink-0"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(7,10,16,0.9)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #d97706, #b45309)" }}
          >
            <Disc3 size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight">
              Music Library
            </h1>
            <p className="text-xs text-gray-600 mt-0.5">
              {loading
                ? "Loading…"
                : `${tracks.length} track${tracks.length !== 1 ? "s" : ""}${hasActiveFilters ? " (filtered)" : ""}`}
            </p>
          </div>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div
        className="px-8 py-4 flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
      >
        <div className="relative max-w-lg">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600"
            size={14}
          />
          <input
            type="text"
            placeholder="Search tracks, artists, moods…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 rounded-xl text-sm text-white placeholder-gray-700 focus:outline-none transition-all"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
            onFocus={(e) =>
              (e.target.style.borderColor = "rgba(217,119,6,0.4)")
            }
            onBlur={(e) =>
              (e.target.style.borderColor = "rgba(255,255,255,0.07)")
            }
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 transition-colors"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* ── Body: sidebar + track list ── */}
      <div className="flex flex-1 min-h-0">
        {/* ── Sidebar filters ── */}
        <div
          className="w-52 flex-shrink-0 overflow-y-auto p-5"
          style={{
            borderRight: "1px solid rgba(255,255,255,0.05)",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255,255,255,0.08) transparent",
          }}
        >
          <FilterSection
            title="Genre"
            items={filters.genres}
            selected={selectedGenre}
            onSelect={setSelectedGenre}
          />
          <FilterSection
            title="Mood"
            items={filters.moods}
            selected={selectedMood}
            onSelect={setSelectedMood}
          />
          <FilterSection
            title="Tempo"
            items={filters.tempos}
            selected={selectedTempo}
            onSelect={setSelectedTempo}
          />

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="w-full mt-2 px-3 py-2 rounded-lg text-xs font-medium text-gray-600 hover:text-amber-400 transition-colors flex items-center justify-center gap-1.5"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <X size={10} /> Clear filters
            </button>
          )}
        </div>

        {/* ── Track list ── */}
        <div
          className="flex-1 overflow-y-auto"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255,255,255,0.08) transparent",
          }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <div
                className="w-7 h-7 rounded-full animate-spin"
                style={{
                  border: "2px solid rgba(217,119,6,0.2)",
                  borderTopColor: "#d97706",
                }}
              />
              <p className="text-sm text-gray-600">Loading tracks…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <p className="text-sm text-red-400">{error}</p>
              <button
                onClick={loadTracks}
                className="text-xs text-amber-500 hover:text-amber-300 transition-colors"
              >
                Try again
              </button>
            </div>
          ) : tracks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <Music size={28} className="text-gray-700" />
              <p className="text-sm text-gray-500">No tracks found</p>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="text-xs text-amber-500 hover:text-amber-300 transition-colors"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr
                  className="text-[10px] font-bold uppercase tracking-widest text-gray-600 sticky top-0"
                  style={{
                    background: "rgba(7,10,16,0.97)",
                    backdropFilter: "blur(8px)",
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                  }}
                >
                  <th className="text-left py-3 pl-6 w-10">#</th>
                  <th className="text-left py-3 pl-3">Track</th>
                  <th className="text-left py-3 hidden lg:table-cell pr-6">
                    Waveform
                  </th>
                  <th className="text-left py-3 hidden md:table-cell">Genre</th>
                  <th className="text-left py-3 hidden md:table-cell">Mood</th>
                  <th className="text-left py-3 hidden sm:table-cell">Tempo</th>
                  <th className="text-right py-3 pr-6">Duration</th>
                </tr>
              </thead>
              <tbody>
                {tracks.map((track, i) => {
                  const isPlaying = playingTrackId === track.id;
                  const isHovered = hoveredRow === track.id;

                  return (
                    <tr
                      key={track.id}
                      className="cursor-pointer transition-all duration-100"
                      style={{
                        background: isHovered
                          ? "rgba(217,119,6,0.05)"
                          : "transparent",
                      }}
                      onMouseEnter={() => setHoveredRow(track.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      onClick={(e) => handlePlayPause(e, track)}
                    >
                      {/* Index / play icon */}
                      <td className="py-3 pl-6 w-10">
                        <div className="w-6 h-6 flex items-center justify-center">
                          {isPlaying ? (
                            <button
                              onClick={(e) => handlePlayPause(e, track)}
                              className="text-amber-400"
                            >
                              <Pause size={13} />
                            </button>
                          ) : isHovered ? (
                            <button
                              onClick={(e) => handlePlayPause(e, track)}
                              className="text-gray-300 hover:text-white"
                            >
                              <Play size={13} />
                            </button>
                          ) : (
                            <span className="text-[11px] text-gray-700 font-mono tabular-nums">
                              {i + 1}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Title + artist */}
                      <td className="py-3 pl-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center"
                            style={{
                              background: isPlaying
                                ? "rgba(217,119,6,0.15)"
                                : "rgba(255,255,255,0.05)",
                              border: isPlaying
                                ? "1px solid rgba(217,119,6,0.3)"
                                : "1px solid transparent",
                              transition: "all 0.2s",
                            }}
                          >
                            {track.thumbnailUrl ? (
                              <img
                                src={track.thumbnailUrl}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <Music
                                size={14}
                                className={
                                  isPlaying ? "text-amber-400" : "text-gray-700"
                                }
                              />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p
                              className={`text-sm font-semibold truncate max-w-[200px] ${isPlaying ? "text-amber-300" : "text-white"}`}
                            >
                              {track.title}
                            </p>
                            <p className="text-[11px] text-gray-600 truncate max-w-[200px] mt-0.5">
                              {track.artist}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Waveform */}
                      <td className="py-3 hidden lg:table-cell pr-6">
                        <WaveformBars isPlaying={isPlaying} count={20} />
                      </td>

                      {/* Genre */}
                      <td className="py-3 hidden md:table-cell">
                        {track.genre && (
                          <span
                            className="text-[10px] font-medium px-2 py-0.5 rounded-md"
                            style={{
                              background: "rgba(255,255,255,0.06)",
                              color: "#9ca3af",
                            }}
                          >
                            {track.genre}
                          </span>
                        )}
                      </td>

                      {/* Mood */}
                      <td className="py-3 hidden md:table-cell">
                        <span className="text-[11px] text-gray-600 capitalize">
                          {track.mood}
                        </span>
                      </td>

                      {/* Tempo */}
                      <td className="py-3 hidden sm:table-cell">
                        <span className="text-[11px] text-gray-600 capitalize">
                          {track.tempo}
                        </span>
                      </td>

                      {/* Duration */}
                      <td className="py-3 pr-6 text-right">
                        <span className="text-[11px] text-gray-600 font-mono tabular-nums">
                          {formatDuration(track.duration)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Status bar ── */}
      <div
        className="flex items-center gap-4 px-8 py-3 flex-shrink-0"
        style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
      >
        {!loading && (
          <>
            <span className="text-sm font-bold text-amber-400">
              {tracks.length}
            </span>
            <span className="text-[11px] text-gray-700">tracks</span>
          </>
        )}

        {playingTrackId &&
          (() => {
            const t = tracks.find((tr) => tr.id === playingTrackId);
            return t ? (
              <div className="flex items-center gap-3 ml-auto">
                <WaveformBars isPlaying count={12} />
                <div>
                  <p className="text-xs font-semibold text-white leading-none">
                    {t.title}
                  </p>
                  <p className="text-[10px] text-gray-600 mt-0.5">{t.artist}</p>
                </div>
                <button
                  onClick={() => {
                    audioRef.current?.pause();
                    setPlayingTrackId(null);
                  }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-600 hover:text-white transition-colors ml-2"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <X size={11} />
                </button>
              </div>
            ) : null;
          })()}
      </div>
    </div>
  );
};

export default MusicLibraryPage;
